import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Building2, ArrowRight, ArrowLeft, Check, Loader2,
  FolderKanban, Users, Share2, Globe, Wrench, Layers, Headphones, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const factors = [
  { icon: FolderKanban, label: 'Number of active projects' },
  { icon: Users, label: 'Number of users' },
  { icon: Share2, label: 'Channel-partner access' },
  { icon: Globe, label: 'API requirements' },
  { icon: Wrench, label: 'CRM and third-party integrations' },
  { icon: Layers, label: 'Custom workflows' },
  { icon: Headphones, label: 'Implementation and onboarding support' },
  { icon: Shield, label: 'White-labelling or enterprise requirements' },
];

const RequestPricingPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    work_email: '',
    phone: '',
    city: '',
    company_website: '',
    number_of_projects: '',
    estimated_number_of_units: '',
    estimated_number_of_users: '',
    channel_partner_access_required: false,
    api_access_required: false,
    crm_used: '',
    integration_requirements: '',
    customisation_requirements: '',
    message: '',
  });

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/pricing-requests/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'website' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit');
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="request-pricing-page">
      {/* Nav */}
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-zinc-950 rounded-sm flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-sm tracking-tight">NineDegree</span>
          </Link>
          <Link to="/" className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {submitted ? (
          <div className="text-center py-20" data-testid="pricing-request-success">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold mb-3">Thank you</h1>
            <p className="text-zinc-500 text-sm max-w-md mx-auto mb-8">
              We&apos;ve received your pricing request. A member of our team will reach out within 24 hours with a customised proposal.
            </p>
            <div className="flex justify-center gap-3">
              <Link to="/signup" className="bg-blue-600 text-white text-sm font-medium px-6 py-2.5 rounded-sm hover:bg-blue-700 inline-flex items-center gap-2">
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/" className="border border-zinc-300 text-zinc-700 text-sm font-medium px-6 py-2.5 rounded-sm hover:bg-zinc-100">
                Back to Home
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left - Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h1 className="text-3xl font-bold tracking-tight mb-3">Pricing built around your operations</h1>
                <p className="text-zinc-500 text-sm leading-relaxed">
                  Pricing starts at <strong className="text-zinc-800">&#8377;5,000 per month</strong>. Your final commercial proposal will depend on the number of projects, users, channel partners, integrations and customisation required.
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-4">Factors Affecting Pricing</p>
                <div className="space-y-3">
                  {factors.map((f) => (
                    <div key={f.label} className="flex items-center gap-3 text-sm text-zinc-600">
                      <f.icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-zinc-200 rounded-sm p-8 shadow-sm">
                <h2 className="text-lg font-bold mb-1">Request Pricing</h2>
                <p className="text-xs text-zinc-500 mb-6">Our team will prepare a customised proposal for you.</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Company Name *</Label>
                      <Input value={form.company_name} onChange={e => update('company_name', e.target.value)} required className="h-9 text-sm" data-testid="pricing-company-name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Your Name *</Label>
                      <Input value={form.contact_name} onChange={e => update('contact_name', e.target.value)} required className="h-9 text-sm" data-testid="pricing-contact-name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Work Email *</Label>
                      <Input type="email" value={form.work_email} onChange={e => update('work_email', e.target.value)} required className="h-9 text-sm" data-testid="pricing-email" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Phone *</Label>
                      <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} required className="h-9 text-sm" data-testid="pricing-phone" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">City</Label>
                      <Input value={form.city} onChange={e => update('city', e.target.value)} className="h-9 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Company Website</Label>
                      <Input value={form.company_website} onChange={e => update('company_website', e.target.value)} className="h-9 text-sm" />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-2">
                    <p className="text-xs font-medium text-zinc-700 mb-3">Your Requirements</p>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Number of Projects</Label>
                        <Input value={form.number_of_projects} onChange={e => update('number_of_projects', e.target.value)} className="h-9 text-sm" placeholder="e.g. 3" data-testid="pricing-projects" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Estimated Units</Label>
                        <Input value={form.estimated_number_of_units} onChange={e => update('estimated_number_of_units', e.target.value)} className="h-9 text-sm" placeholder="e.g. 500" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Estimated Users</Label>
                        <Input value={form.estimated_number_of_users} onChange={e => update('estimated_number_of_users', e.target.value)} className="h-9 text-sm" placeholder="e.g. 15" />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-6">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={form.channel_partner_access_required} onCheckedChange={v => update('channel_partner_access_required', v)} id="cp" />
                      <Label htmlFor="cp" className="text-xs text-zinc-600">Channel partner access needed</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={form.api_access_required} onCheckedChange={v => update('api_access_required', v)} id="api" />
                      <Label htmlFor="api" className="text-xs text-zinc-600">API access needed</Label>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Current CRM (if any)</Label>
                    <Input value={form.crm_used} onChange={e => update('crm_used', e.target.value)} className="h-9 text-sm" placeholder="e.g. Salesforce, None" />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Additional Requirements</Label>
                    <Textarea value={form.message} onChange={e => update('message', e.target.value)} className="text-sm min-h-[80px]" placeholder="Any specific integrations, workflows, or customisation needs..." data-testid="pricing-message" />
                  </div>

                  <Button type="submit" className="w-full h-10" disabled={loading} data-testid="pricing-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request Pricing'}
                  </Button>

                  <p className="text-[10px] text-zinc-400 text-center">
                    By submitting, you agree to be contacted by our team regarding your enquiry.
                  </p>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestPricingPage;
